/**
 * Campaign Editor Component - STORY-050
 *
 * WYSIWYG-like editor for creating newsletter campaigns:
 * - HTML content editing with live preview
 * - Template variable support
 * - Segment selection
 * - Scheduling options
 */

import { useState, useEffect } from 'react'
import {
  Eye,
  Code,
  Copy,
  Check,
  Calendar,
  Users,
  Send,
  Save,
  ArrowLeft,
  Bold,
  Italic,
  Link,
  Image,
  List,
  Heading1,
  Heading2,
  AlignLeft,
  AlignCenter,
  Type,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { useSegments } from '@/hooks/useMarketing'
import type { Campaign, CampaignFormData, CampaignType } from '@/types/marketing'
import { CAMPAIGN_TYPE_LABELS } from '@/types/marketing'

interface CampaignEditorProps {
  campaign?: Campaign | null
  onSave: (data: CampaignFormData) => Promise<void>
  onSchedule?: (data: CampaignFormData, scheduledAt: string) => Promise<void>
  onSend?: (data: CampaignFormData) => Promise<void>
  onBack: () => void
  isLoading?: boolean
}

// Default email template
const DEFAULT_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #10b981; color: white; padding: 30px; text-align: center; border-radius: 8px 8px 0 0; }
    .header h1 { margin: 0; font-size: 28px; }
    .content { background: #f9fafb; padding: 30px; border-radius: 0 0 8px 8px; }
    .button { display: inline-block; background: #10b981; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin-top: 20px; }
    .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{entreprise_nom}}</h1>
    </div>
    <div class="content">
      <p>Bonjour {{client_name}},</p>
      <p>Votre contenu ici...</p>
      <p>Cordialement,<br>L'equipe {{entreprise_nom}}</p>
    </div>
    <div class="footer">
      <p>Cet email a ete envoye automatiquement.</p>
    </div>
  </div>
</body>
</html>`

// Available template variables
const TEMPLATE_VARIABLES = [
  '{{client_name}}',
  '{{client_email}}',
  '{{entreprise_nom}}',
  '{{date}}',
  '{{lien_desinscription}}',
]

export default function CampaignEditor({
  campaign,
  onSave,
  onSchedule,
  onSend,
  onBack,
  isLoading = false,
}: CampaignEditorProps) {
  const [formData, setFormData] = useState<CampaignFormData>({
    name: campaign?.name || '',
    subject: campaign?.subject || '',
    type: campaign?.type || 'newsletter',
    content_html: campaign?.content_html || DEFAULT_TEMPLATE,
    content_text: campaign?.content_text || null,
    segment_id: campaign?.segment_id || null,
    scheduled_at: campaign?.scheduled_at || null,
    metadata: campaign?.metadata || null,
  })

  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'split'>('split')
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [scheduleDate, setScheduleDate] = useState('')
  const [scheduleTime, setScheduleTime] = useState('')
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data: segments } = useSegments()

  // Update form when campaign changes
  useEffect(() => {
    if (campaign) {
      setFormData({
        name: campaign.name,
        subject: campaign.subject,
        type: campaign.type,
        content_html: campaign.content_html,
        content_text: campaign.content_text,
        segment_id: campaign.segment_id,
        scheduled_at: campaign.scheduled_at,
        metadata: campaign.metadata,
      })
    }
  }, [campaign])

  const selectedSegment = segments?.find(s => s.id === formData.segment_id)

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom de la campagne est requis'
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis'
    }
    if (!formData.content_html.trim()) {
      newErrors.content_html = 'Le contenu est requis'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return
    await onSave(formData)
  }

  const handleSchedule = async () => {
    if (!validateForm()) return
    if (!scheduleDate || !scheduleTime) {
      setErrors({ ...errors, schedule: 'Date et heure requises' })
      return
    }

    const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
    if (onSchedule) {
      await onSchedule(formData, scheduledAt)
    }
    setShowScheduleModal(false)
  }

  const handleSend = async () => {
    if (!validateForm()) return
    if (onSend) {
      await onSend(formData)
    }
  }

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable)
    setCopiedVariable(variable)
    setTimeout(() => setCopiedVariable(null), 2000)
  }

  const insertAtCursor = (text: string) => {
    const textarea = document.getElementById('content-editor') as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const content = formData.content_html
    const newContent = content.substring(0, start) + text + content.substring(end)

    setFormData({ ...formData, content_html: newContent })

    // Reset cursor position
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + text.length, start + text.length)
    }, 0)
  }

  const insertHtmlTag = (tag: string, attributes = '') => {
    const openTag = attributes ? `<${tag} ${attributes}>` : `<${tag}>`
    const closeTag = `</${tag}>`
    insertAtCursor(`${openTag}${closeTag}`)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-beige-200 bg-white">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Retour
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-brown-900">
              {campaign ? 'Modifier la campagne' : 'Nouvelle campagne'}
            </h1>
            {campaign && (
              <Badge variant="beige" size="sm">
                {CAMPAIGN_TYPE_LABELS[campaign.type]}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center bg-beige-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'edit'
                  ? 'bg-white text-brown-900 shadow-sm'
                  : 'text-brown-600 hover:text-brown-900'
              }`}
            >
              <Code className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'split'
                  ? 'bg-white text-brown-900 shadow-sm'
                  : 'text-brown-600 hover:text-brown-900'
              }`}
            >
              Split
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'preview'
                  ? 'bg-white text-brown-900 shadow-sm'
                  : 'text-brown-600 hover:text-brown-900'
              }`}
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <Button
            variant="outline"
            onClick={handleSave}
            isLoading={isLoading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Sauvegarder
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowScheduleModal(true)}
            leftIcon={<Calendar className="w-4 h-4" />}
          >
            Planifier
          </Button>
          <Button
            onClick={handleSend}
            isLoading={isLoading}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Envoyer
          </Button>
        </div>
      </div>

      {/* Campaign settings bar */}
      <div className="flex items-center gap-6 p-4 bg-beige-50 border-b border-beige-200">
        <div className="flex-1 max-w-xs">
          <Input
            label="Nom de la campagne"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Newsletter Janvier 2024"
            error={errors.name}
          />
        </div>
        <div className="flex-1 max-w-md">
          <Input
            label="Sujet de l'email"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Ex: Les dernieres actualites de..."
            error={errors.subject}
          />
        </div>
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-medium text-brown-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as CampaignType })}
            className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
          >
            {Object.entries(CAMPAIGN_TYPE_LABELS).map(([type, label]) => (
              <option key={type} value={type}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-medium text-brown-700 mb-1">Destinataires</label>
          <select
            value={formData.segment_id || ''}
            onChange={(e) => setFormData({ ...formData, segment_id: e.target.value || null })}
            className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
          >
            <option value="">Tous les abonnes</option>
            {segments?.map((segment) => (
              <option key={segment.id} value={segment.id}>
                {segment.name} ({segment.contact_count} contacts)
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm text-brown-600">
          <Users className="w-4 h-4" />
          <span>{selectedSegment?.contact_count || 'Tous'} destinataires</span>
        </div>
      </div>

      {/* Editor area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Code editor */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div className={`flex flex-col ${viewMode === 'split' ? 'w-1/2' : 'w-full'} border-r border-beige-200`}>
            {/* Toolbar */}
            <div className="flex items-center gap-1 p-2 border-b border-beige-200 bg-white">
              <button
                onClick={() => insertHtmlTag('strong')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Gras"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertHtmlTag('em')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Italique"
              >
                <Italic className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-beige-200 mx-1" />
              <button
                onClick={() => insertHtmlTag('h1')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Titre 1"
              >
                <Heading1 className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertHtmlTag('h2')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Titre 2"
              >
                <Heading2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertHtmlTag('p')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Paragraphe"
              >
                <Type className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-beige-200 mx-1" />
              <button
                onClick={() => insertHtmlTag('a', 'href=""')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Lien"
              >
                <Link className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertAtCursor('<img src="" alt="" />')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Image"
              >
                <Image className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertHtmlTag('ul')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Liste"
              >
                <List className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-beige-200 mx-1" />
              <button
                onClick={() => insertAtCursor('<div style="text-align: left;">')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Aligner a gauche"
              >
                <AlignLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => insertAtCursor('<div style="text-align: center;">')}
                className="p-2 hover:bg-beige-100 rounded"
                title="Centrer"
              >
                <AlignCenter className="w-4 h-4" />
              </button>
            </div>

            {/* Variables */}
            <div className="flex items-center gap-2 p-2 bg-beige-50 border-b border-beige-200 overflow-x-auto">
              <span className="text-xs text-brown-500 flex-shrink-0">Variables:</span>
              {TEMPLATE_VARIABLES.map((variable) => (
                <button
                  key={variable}
                  onClick={() => copyVariable(variable)}
                  className="inline-flex items-center gap-1 text-xs bg-white border border-beige-200 text-brown-600 px-2 py-1 rounded hover:bg-beige-100 transition-colors flex-shrink-0"
                >
                  <Code className="w-3 h-3" />
                  {variable}
                  {copiedVariable === variable ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              ))}
            </div>

            {/* Editor */}
            <textarea
              id="content-editor"
              value={formData.content_html}
              onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
              className="flex-1 w-full p-4 font-mono text-sm resize-none focus:outline-none"
              placeholder="Entrez le contenu HTML de votre email..."
            />
          </div>
        )}

        {/* Preview */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className={`flex flex-col ${viewMode === 'split' ? 'w-1/2' : 'w-full'} bg-beige-100`}>
            <div className="p-2 bg-white border-b border-beige-200">
              <span className="text-sm text-brown-600">Apercu</span>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-2xl mx-auto">
                {/* Email header simulation */}
                <div className="p-3 bg-beige-50 border-b border-beige-200">
                  <p className="text-xs text-brown-500">De: {formData.name || 'Votre Entreprise'}</p>
                  <p className="text-sm font-medium text-brown-900 mt-1">
                    Sujet: {formData.subject || '(Aucun sujet)'}
                  </p>
                </div>
                {/* Email content */}
                <div
                  className="p-4"
                  dangerouslySetInnerHTML={{
                    __html: formData.content_html
                      .replace(/\{\{client_name\}\}/g, 'Jean Dupont')
                      .replace(/\{\{client_email\}\}/g, 'jean.dupont@example.com')
                      .replace(/\{\{entreprise_nom\}\}/g, 'LesSablesWeb')
                      .replace(/\{\{date\}\}/g, new Date().toLocaleDateString('fr-FR'))
                      .replace(/\{\{lien_desinscription\}\}/g, '#'),
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Planifier l'envoi"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Date d'envoi
            </label>
            <input
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Heure d'envoi
            </label>
            <input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>
          {errors.schedule && (
            <p className="text-sm text-red-600">{errors.schedule}</p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setShowScheduleModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSchedule} isLoading={isLoading}>
              Planifier
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
